import { useEffect, useRef } from 'react'
import { useLocation, useNavigate, useNavigationType } from 'react-router-dom'
import { isTauri } from '@/lib/utils/tauri'

/**
 * Two-finger trackpad swipe to go back/forward, the way Safari and Chrome do it.
 *
 * The webview does not give us the native gesture (wry leaves
 * `allowsBackForwardNavigationGestures` off, and turning it on would drive the
 * webview's own history rather than the router), so the gesture is
 * reconstructed from the horizontal `wheel` stream a trackpad produces:
 *
 * - a gesture is claimed only when the wheel stream is clearly horizontal and
 *   no ancestor scroller can still absorb it in that direction — same
 *   precedence rule the browsers use, so swiping a wide table scrolls the
 *   table instead of navigating;
 * - an edge bubble tracks the pull the whole time the fingers are down. The
 *   direction locks for that physical gesture, so dragging back only cancels
 *   it rather than unexpectedly turning Back into Forward;
 * - nothing commits mid-pull. Navigation happens on release, and only if the
 *   pull ended past the threshold — the arm state of the bubble is the
 *   promise. On macOS, the native shell supplies the trackpad release phase so
 *   inertial scrolling cannot arm a gesture after the user's fingers are up.
 *   Other desktop platforms use a short wheel-stream silence fallback.
 *
 * Desktop only — the hook is a no-op outside Tauri, where the real gesture is
 * already handled by the browser.
 */

/** Accumulated horizontal distance (px) that commits the navigation. */
const COMMIT_DISTANCE = 140
/** Distance before a horizontal stream is claimed as navigation. */
const INTENT_DISTANCE = 12
/** Horizontal dominance required before a wheel stream counts as a swipe. */
const DIRECTION_RATIO = 1.6
/** Silence (ms) after the last wheel event that counts as the fingers lifting. */
const GESTURE_END_MS = 90
/** Safety net if macOS fails to deliver a native terminal scroll phase. */
const NATIVE_PHASE_FAILSAFE_MS = 500
/** Bubble travel between fully hidden and fully revealed. */
const HIDDEN_OFFSET = 56
const SHOWN_OFFSET = 14

type SwipeDirection = -1 | 1
type TrackpadPhase =
  | 'began'
  | 'ended'
  | 'cancelled'
  | 'momentum-began'
  | 'momentum-ended'

const CHEVRON_LEFT = 'M14.5 5.5 8.5 12l6 6.5'
const CHEVRON_RIGHT = 'M9.5 5.5 15.5 12l-6 6.5'

/**
 * Walks up from the wheel target looking for a scroller that still has room to
 * move in the swipe direction (or an explicit `data-no-swipe-nav` opt-out).
 * Anything it finds owns the gesture, not us.
 */
function isGestureAbsorbed(
  target: EventTarget | null,
  direction: SwipeDirection,
): boolean {
  let node: Element | null =
    target instanceof Element
      ? target
      : target instanceof Node
        ? target.parentElement
        : null

  while (node) {
    if (node.hasAttribute('data-no-swipe-nav')) return true

    const overflowX = getComputedStyle(node).overflowX
    if (overflowX === 'auto' || overflowX === 'scroll' || overflowX === 'overlay') {
      const maxScroll = node.scrollWidth - node.clientWidth
      if (maxScroll > 1) {
        // Back (deltaX < 0) consumes scrollLeft, forward adds to it.
        const room =
          direction === -1 ? node.scrollLeft : maxScroll - node.scrollLeft
        if (room > 1) return true
      }
    }

    node = node.parentElement
  }

  return false
}

interface Indicator {
  update: (direction: SwipeDirection, progress: number) => void
  /** `committed` fades the bubble out in place; otherwise it slides back off. */
  release: (committed: boolean) => void
  destroy: () => void
}

function createIndicator(): Indicator {
  const root = document.createElement('div')
  root.className = 'swipe-nav-indicator'
  root.setAttribute('aria-hidden', 'true')
  root.innerHTML =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
    'stroke-width="2.25" stroke-linecap="round" stroke-linejoin="round">' +
    `<path d="${CHEVRON_LEFT}" /></svg>`
  document.body.appendChild(root)

  const path = root.querySelector('path') as SVGPathElement

  const place = (direction: SwipeDirection, progress: number) => {
    const armed = progress >= 1
    const offset = -HIDDEN_OFFSET + progress * (HIDDEN_OFFSET + SHOWN_OFFSET)
    root.style.opacity = String(Math.min(1, progress * 1.7))
    root.style.transform =
      `translateX(${direction === -1 ? offset : -offset}px)` +
      ` translateY(-50%) scale(${armed ? 1.08 : 1})`
    root.dataset.armed = armed ? 'true' : 'false'
  }

  return {
    update: (direction, progress) => {
      root.dataset.side = direction === -1 ? 'left' : 'right'
      root.dataset.dragging = 'true'
      path.setAttribute('d', direction === -1 ? CHEVRON_LEFT : CHEVRON_RIGHT)
      place(direction, progress)
    },
    release: (committed: boolean) => {
      const direction: SwipeDirection = root.dataset.side === 'right' ? 1 : -1
      root.dataset.dragging = 'false'
      if (committed) {
        root.style.opacity = '0'
        return
      }
      place(direction, 0)
    },
    destroy: () => root.remove(),
  }
}

export function useSwipeNavigation(): void {
  const navigate = useNavigate()
  const location = useLocation()
  const navigationType = useNavigationType()
  // React Router stamps its session-history index onto history.state, which is
  // what tells us whether there is anywhere to swipe to. Only a PUSH drops the
  // forward entries, so that is the one case where the high-water mark resets;
  // a REPLACE leaves them in place.
  const furthestIndexRef = useRef(0)

  useEffect(() => {
    const index = (window.history.state as { idx?: number } | null)?.idx
    if (typeof index !== 'number') return

    furthestIndexRef.current =
      navigationType === 'PUSH'
        ? index
        : Math.max(furthestIndexRef.current, index)
  }, [location, navigationType])

  useEffect(() => {
    if (!isTauri()) return

    const indicator = createIndicator()

    let active = false
    let claimed = false
    let rejected = false
    let direction: SwipeDirection = -1
    let accumulatedX = 0
    let accumulatedY = 0
    let suppressMomentum = false
    const usesNativePhases = navigator.platform.startsWith('Mac')
    let endTimer: ReturnType<typeof setTimeout> | undefined
    let phaseEndTimer: ReturnType<typeof setTimeout> | undefined
    let unlistenTrackpadPhase: (() => void) | undefined
    let disposed = false

    const canNavigate = (towards: SwipeDirection) => {
      const index = (window.history.state as { idx?: number } | null)?.idx
      if (typeof index !== 'number') {
        return towards === -1 ? window.history.length > 1 : false
      }
      return towards === -1 ? index > 0 : index < furthestIndexRef.current
    }

    const endGesture = (cancelled = false) => {
      const committed =
        !cancelled && claimed && accumulatedX * direction >= COMMIT_DISTANCE
      const wasClaimed = claimed
      const settledDirection = direction

      active = false
      claimed = false
      rejected = false
      accumulatedX = 0
      accumulatedY = 0

      if (endTimer) {
        clearTimeout(endTimer)
        endTimer = undefined
      }

      if (wasClaimed) indicator.release(committed)
      if (committed) navigate(settledDirection)
    }

    const scheduleFallbackEnd = () => {
      if (endTimer) clearTimeout(endTimer)
      endTimer = setTimeout(
        () => endGesture(),
        usesNativePhases ? NATIVE_PHASE_FAILSAFE_MS : GESTURE_END_MS,
      )
    }

    // Native AppKit scroll phases are forwarded by the Tauri shell. DOM wheel
    // events do not expose them, and waiting for their momentum tail is what
    // previously made navigation feel delayed. The zero-delay task lets the
    // final wheel event from the same AppKit dispatch land before we settle.
    void import('@tauri-apps/api/event')
      .then(({ listen }) =>
        listen<TrackpadPhase>('trackpad-scroll-phase', event => {
          if (event.payload === 'began') {
            suppressMomentum = false
            return
          }

          if (event.payload === 'ended' || event.payload === 'momentum-began') {
            suppressMomentum = true
            if (phaseEndTimer) clearTimeout(phaseEndTimer)
            phaseEndTimer = setTimeout(() => endGesture(), 0)
            return
          }

          if (event.payload === 'cancelled') {
            suppressMomentum = true
            if (phaseEndTimer) clearTimeout(phaseEndTimer)
            phaseEndTimer = setTimeout(() => endGesture(true), 0)
          }
        }),
      )
      .then(unlisten => {
        if (disposed) unlisten()
        else unlistenTrackpadPhase = unlisten
      })
      .catch(() => {
        // Non-macOS Tauri builds have no native phase event. Their wheel
        // stream still settles through the short silence fallback.
      })

    const handleWheel = (event: WheelEvent) => {
      // Line/page deltas come from classic mouse wheels; ctrl+wheel is a pinch.
      if (event.deltaMode !== 0 || event.ctrlKey) return
      if (suppressMomentum) return

      scheduleFallbackEnd()

      if (!active) {
        active = true
        rejected = false
        accumulatedX = 0
        accumulatedY = 0
      }

      if (rejected) return

      accumulatedX += event.deltaX
      accumulatedY += event.deltaY

      if (!claimed) {
        const horizontal = Math.abs(accumulatedX)
        const vertical = Math.abs(accumulatedY)
        if (Math.max(horizontal, vertical) < INTENT_DISTANCE) return

        if (horizontal < vertical * DIRECTION_RATIO) {
          rejected = true
          return
        }

        direction = accumulatedX < 0 ? -1 : 1
        claimed =
          canNavigate(direction) && !isGestureAbsorbed(event.target, direction)
        if (!claimed) {
          rejected = true
          return
        }
      }

      if (!claimed) return

      // A stream that turns vertical mid-swipe was a diagonal scroll, not a
      // navigation gesture.
      if (Math.abs(accumulatedY) > Math.abs(accumulatedX) * 1.2) {
        claimed = false
        rejected = true
        indicator.release(false)
        return
      }

      // Lock direction until release. Pulling back reduces progress all the way
      // to zero, but can never turn this same gesture into the opposite action.
      if (accumulatedX * direction < 0) {
        accumulatedX = 0
      }

      const pulled = Math.max(0, accumulatedX * direction)
      indicator.update(direction, Math.min(1, pulled / COMMIT_DISTANCE))
    }

    window.addEventListener('wheel', handleWheel, { passive: true })

    return () => {
      disposed = true
      window.removeEventListener('wheel', handleWheel)
      if (endTimer) clearTimeout(endTimer)
      if (phaseEndTimer) clearTimeout(phaseEndTimer)
      unlistenTrackpadPhase?.()
      indicator.destroy()
    }
  }, [navigate])
}
